import { getBaseRole } from '@/helpers/customRoles';
import { ProjectMemberDraft, TaskTrackerSearchUser } from './projectMember.types'
import { TaskTrackerMemberSearchUser } from './taskTrackerUser.types'
import {
  TaskTrackerProjectMember,
  TaskTrackerProjectRole,
} from '../store/taskTracker.types'

export const PROJECT_SYSTEM_ROLE_TITLES = {
  admin: 'Администратор',
  employee: 'Сотрудник',
} as const

export const resolveProjectRoleIds = (roles: TaskTrackerProjectRole[]) => {
  const adminRole = roles.find(
    (role) => role.title === PROJECT_SYSTEM_ROLE_TITLES.admin,
  )
  const employeeRole = roles.find(
    (role) => role.title === PROJECT_SYSTEM_ROLE_TITLES.employee,
  )

  return {
    admin: adminRole?.id ?? null,
    employee: employeeRole?.id ?? null,
  }
}

export const buildProjectRoleOptions = (roleIds: {
  admin: number | null
  employee: number | null
}) => {
  const options: Array<{ value: string; label: string }> = []

  if (roleIds.employee != null) {
    options.push({
      value: String(roleIds.employee),
      label: PROJECT_SYSTEM_ROLE_TITLES.employee,
    })
  }

  if (roleIds.admin != null) {
    options.push({
      value: String(roleIds.admin),
      label: PROJECT_SYSTEM_ROLE_TITLES.admin,
    })
  }

  return options
}

export const mergeProjectRoles = (
  current: TaskTrackerProjectRole[],
  incoming: TaskTrackerProjectRole[],
): TaskTrackerProjectRole[] => {
  const map = new Map<number, TaskTrackerProjectRole>()
  current.forEach((role) => map.set(role.id, role))
  incoming.forEach((role) => map.set(role.id, role))
  return Array.from(map.values()).sort((a, b) =>
    a.title.localeCompare(b.title, 'ru'),
  )
}

export const getMemberInitial = (name: string) =>
  name.trim().slice(0, 1).toUpperCase() || '?'

export const formatMemberContact = (
  email: string | null,
  phone: string | null,
): string => [email, phone].filter(Boolean).join(' · ')

export const getMemberDisplayLabel = (
  member: Pick<ProjectMemberDraft, 'name' | 'email' | 'phone'>,
): string => {
  const contact = formatMemberContact(member.email, member.phone)
  return contact ? `${member.name} — ${contact}` : member.name
}

export const mapProjectMemberToDraft = (
  member: TaskTrackerProjectMember,
): ProjectMemberDraft => ({
  memberId: member.id,
  userId: member.userId,
  name: member.user?.name ?? `Пользователь #${member.userId}`,
  email: member.user?.email ?? null,
  phone: member.user?.phone ?? null,
  avatarPath: member.user?.avatarPath ?? null,
  role:
    member.role?.title === PROJECT_SYSTEM_ROLE_TITLES.admin
      ? 'admin'
      : 'employee',
  roleId: member.role?.id ?? member.roleId,
})

export const mapSearchUserToMemberDraft = (
  user: TaskTrackerSearchUser,
): ProjectMemberDraft => ({
  userId: user.id,
  name: user.name,
  email: user.email,
  phone: user.phone,
  avatarPath: user.avatarPath,
  role: 'employee',
})

export const hasProjectAdminMember = (
  members: Pick<ProjectMemberDraft, 'role'>[],
): boolean => members.some((member) => getBaseRole(member.role) === 'admin')

export const TASK_TRACKER_LAST_PROJECT_ADMIN_ERROR =
  'Из проекта нельзя убрать последнего администратора'

export const hasAnotherProjectAdminBesides = (
  members: Pick<ProjectMemberDraft, 'userId' | 'role'>[],
  userId: number,
): boolean =>
  members.some(
    (member) => member.userId !== userId && getBaseRole(member.role) === 'admin',
  )

export const canProjectAdminChangeOwnRole = (
  members: Pick<ProjectMemberDraft, 'userId' | 'role'>[],
  userId: number,
  nextRole: ProjectMemberDraft['role'],
): boolean => {
  const currentMember = members.find((member) => member.userId === userId)
  if (getBaseRole(currentMember?.role) !== 'admin') return true
  if (getBaseRole(nextRole) === 'admin') return true

  return hasAnotherProjectAdminBesides(members, userId)
}

export const canProjectAdminRemoveSelf = (
  members: Pick<ProjectMemberDraft, 'userId' | 'role'>[],
  userId: number,
): boolean => {
  const currentMember = members.find((member) => member.userId === userId)
  if (getBaseRole(currentMember?.role) !== 'admin') return true

  return hasAnotherProjectAdminBesides(members, userId)
}

export const matchesMemberSearchQuery = (
  item: Pick<TaskTrackerMemberSearchUser, 'name' | 'email' | 'phone'>,
  searchQuery: string,
) => {
  const normalizedQuery = searchQuery.trim().toLowerCase()
  if (!normalizedQuery) return true

  const haystack = [item.name, item.email ?? '', item.phone ?? '']
    .join(' ')
    .toLowerCase()

  return haystack.includes(normalizedQuery)
}

export const mapProfileToMemberSearchUser = (
  profile: Record<string, unknown> | null | undefined,
): TaskTrackerMemberSearchUser | null => {
  if (!profile) return null

  const id = Number(profile.id)
  if (!Number.isFinite(id) || id <= 0) return null

  const name = typeof profile.name === 'string' ? profile.name.trim() : ''

  return {
    id,
    name: name || 'Без имени',
    email: typeof profile.email === 'string' ? profile.email : null,
    phone: typeof profile.phone === 'string' ? profile.phone : null,
    avatarPath:
      typeof profile.avatar_path === 'string'
        ? profile.avatar_path
        : typeof profile.avatarPath === 'string'
          ? profile.avatarPath
          : null,
  }
}
